import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdvancedInsightsFeedback } from './AdvancedInsightsFeedback';

// Matches the body padding-top that clears the fixed portal header
const PORTAL_HEADER_FALLBACK = 105;

// Height of the confidentiality bar along the bottom of the tab (.ai-marking)
const MARKING_HEIGHT = 34;

// A report marks itself confidential with <meta name="classification" content="...">
const markingOf = (doc) => {
    const meta = doc && doc.querySelector('meta[name="classification"]');
    return meta ? (meta.getAttribute('content') || '').trim() : '';
};

// The report is a standalone page with its own styles and scripts, so it stays isolated in an iframe.
// The iframe is sized to the report's full height so the portal page does the scrolling, and the
// report features that assume they own the window are re-homed onto the portal page.
const EMBED_CSS = `
    html, body { overflow: hidden !important; }
    header.top { display: none !important; }
    .zoom-pill {
        position: absolute !important;
        bottom: auto !important;
        top: var(--portal-view-bottom, 0px) !important;
        transform: translate(-50%, calc(-100% - 18px)) !important;
    }
    [id] { scroll-margin-top: var(--portal-section-offset, 0px); }
`;

export function AdvancedInsights(props) {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const frameRef = useRef(null);
    const navRef = useRef(null);
    const scrollToSectionRef = useRef(() => { });
    const [reportDoc, setReportDoc] = useState(null);
    const [height, setHeight] = useState(0);
    const [title, setTitle] = useState('');
    const [updated, setUpdated] = useState('');
    const [sections, setSections] = useState([]);
    const [currentSection, setCurrentSection] = useState(null);
    const [headerHeight, setHeaderHeight] = useState(PORTAL_HEADER_FALLBACK);
    const [atEnd, setAtEnd] = useState(false);
    useEffect(() => props.SiteGUIDSetter(id), [id]);

    // Leaves room for the portal header and the section bar above a section once it is scrolled to
    const sectionOffset = () => headerHeight + (navRef.current ? navRef.current.offsetHeight : 0) + 12;

    useEffect(() => {
        const header = document.querySelector('.navbar.fixed-top');
        if (header) {
            setHeaderHeight(header.getBoundingClientRect().height);
        }
    }, []);

    useEffect(() => {
        if (!reportDoc) {
            return;
        }
        const frame = frameRef.current;

        if (!reportDoc.getElementById('portal-embed')) {
            const style = reportDoc.createElement('style');
            style.id = 'portal-embed';
            style.textContent = EMBED_CSS;
            reportDoc.head.appendChild(style);
        }

        // Title for the band: the report's brand, its separate text pieces joined (newer reports split it into spans)
        const brand = reportDoc.querySelector('header.top .brand');
        const titleParts = [];
        if (brand) {
            const walker = reportDoc.createTreeWalker(brand, NodeFilter.SHOW_TEXT);
            while (walker.nextNode()) {
                const text = walker.currentNode.textContent.trim();
                if (text) {
                    titleParts.push(text);
                }
            }
        }
        setTitle(titleParts.join(' · ') || reportDoc.title);

        // lastModified comes from the Last-Modified header the API sets from the stored report
        const lastModified = new Date(reportDoc.lastModified);
        setUpdated(isNaN(lastModified) ? '' : lastModified.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-AU', { day: 'numeric', month: 'short', year: 'numeric' }));

        // The report's own sticky section nav, rebuilt as a sticky bar on the portal page
        const reportSections = [...reportDoc.querySelectorAll('header.top nav a[href^="#"]')]
            .map((link) => ({ id: link.getAttribute('href').slice(1), label: link.textContent.trim() }))
            .filter((section) => section.id);
        setSections(reportSections);

        const fitToContent = () => setHeight(Math.ceil(reportDoc.documentElement.getBoundingClientRect().height));
        const resizeObserver = new ResizeObserver(fitToContent);
        resizeObserver.observe(reportDoc.body);
        fitToContent();

        // Keeps window-pinned report controls (the chart zoom pill) at the bottom of what is visible: above the
        // confidentiality bar while it rides the bottom of the window, and inside the report at the end of the page
        const markingHeight = markingOf(reportDoc) ? MARKING_HEIGHT : 0;
        const trackVisibleBottom = () => {
            const frameBox = frame.getBoundingClientRect();
            const visibleBottom = Math.max(0, Math.min(frameBox.height, window.innerHeight - markingHeight - frameBox.top));
            reportDoc.documentElement.style.setProperty('--portal-view-bottom', visibleBottom + 'px');
        };

        // Highlights the section being read: the last one whose top has passed under the section bar,
        // or the last section once the page can't scroll any further
        const trackCurrentSection = () => {
            const readingLine = sectionOffset() + 1;
            const frameTop = frame.getBoundingClientRect().top;
            let current = null;
            reportSections.forEach((section) => {
                const element = reportDoc.getElementById(section.id);
                if (element && frameTop + element.getBoundingClientRect().top <= readingLine) {
                    current = section.id;
                }
            });
            const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            if (atBottom && reportSections.length > 0 && current !== null) {
                current = reportSections[reportSections.length - 1].id;
            }
            setCurrentSection(current);
        };

        // The bottom bar is square while it rides the bottom of the window and takes the tab's rounded corners once it
        // comes to rest at the end
        const trackEnd = () => setAtEnd(frame.parentElement.getBoundingClientRect().bottom <= window.innerHeight + 1);

        const onPortalScroll = () => {
            trackVisibleBottom();
            trackCurrentSection();
            trackEnd();
        };
        window.addEventListener('scroll', onPortalScroll, { passive: true });
        window.addEventListener('resize', onPortalScroll);
        onPortalScroll();

        scrollToSectionRef.current = (sectionId) => {
            const target = reportDoc.getElementById(sectionId);
            if (target) {
                const top = window.scrollY + frame.getBoundingClientRect().top + target.getBoundingClientRect().top - sectionOffset();
                window.scrollTo({ top: top, behavior: 'smooth' });
            }
        };

        // In-report links would otherwise try to scroll the iframe, which no longer scrolls
        const routeInReportLinks = (e) => {
            const link = e.target.closest && e.target.closest('a[href^="#"]');
            if (link && link.getAttribute('href').length > 1) {
                e.preventDefault();
                scrollToSectionRef.current(link.getAttribute('href').slice(1));
            }
        };
        reportDoc.addEventListener('click', routeInReportLinks);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', onPortalScroll);
            window.removeEventListener('resize', onPortalScroll);
            reportDoc.removeEventListener('click', routeInReportLinks);
        };
    }, [reportDoc, headerHeight]);

    // The report's own scrollIntoView calls land below the portal header and section bar too,
    // following the bar's height as it wraps on narrower screens
    useEffect(() => {
        if (!reportDoc || !navRef.current) {
            return;
        }
        const syncSectionOffset = () => reportDoc.documentElement.style.setProperty('--portal-section-offset', sectionOffset() + 'px');
        const navObserver = new ResizeObserver(syncSectionOffset);
        navObserver.observe(navRef.current);
        return () => navObserver.disconnect();
    }, [reportDoc, headerHeight, sections]);

    const marking = markingOf(reportDoc);

    return (
        <div className={'advanced-insights' + (marking ? ' has-marking' : '') + (atEnd ? ' is-at-end' : '')}>
            <header className="advanced-insights-band">
                <div className="advanced-insights-band-eyebrow">
                    <i className="fad fa-sparkles"></i>
                    {t('components.layout.advancedinsights')}
                </div>
                <h1 className="advanced-insights-band-title">{title || '\u00a0'}</h1>
                <div className="advanced-insights-band-sub">{t('components.advancedinsights.preparedby')}</div>
                {updated ? (
                    <div className="advanced-insights-band-chips">
                        <span>{t('components.advancedinsights.updated', { date: updated })}</span>
                    </div>
                ) : (
                    <></>
                )}
            </header>
            {sections.length > 0 ? (
                <nav ref={navRef} className="advanced-insights-nav" style={{ top: headerHeight }}>
                    <i className="fad fa-sparkles advanced-insights-glow"></i>
                    {sections.map((section) => (
                        <a key={section.id} href={'#' + section.id}
                            className={section.id === currentSection ? 'is-current' : undefined}
                            onClick={(e) => { e.preventDefault(); scrollToSectionRef.current(section.id); }}>
                            {section.label}
                        </a>
                    ))}
                </nav>
            ) : (
                <></>
            )}
            <iframe
                ref={frameRef}
                className="advanced-insights-frame"
                title={t('components.layout.advancedinsights')}
                src={'/api/AdvancedInsightsAPI/Get?siteGUID=' + encodeURIComponent(id)}
                scrolling="no"
                style={{ height: height ? height + 'px' : '100vh' }}
                onLoad={() => setReportDoc(frameRef.current.contentDocument)}
            />
            {/* Rides the bottom of the window while the report scrolls and comes to rest at the end of the tab, so the
                page footer stays reachable. Holds the confidentiality bar and the feedback button above it. */}
            <div className="advanced-insights-dock">
                {marking ? <div className="ai-marking" role="note">{marking}</div> : <></>}
                <AdvancedInsightsFeedback reportDoc={reportDoc} frameRef={frameRef} siteGUID={id} sections={sections} />
            </div>
        </div>
    );
}
